<template>
  <div class="min-h-screen bg-gradient-to-b from-slate-50 to-white">
    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-16">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div class="text-center">
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 mb-6">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span class="text-sm font-semibold">Real-World Performance Test</span>
          </div>
          
          <h1 class="text-5xl font-bold mb-4">
            InvestBuddy<span class="text-green-600">.ai</span> vs VOO Benchmark
          </h1>
          <p class="text-xl opacity-90 max-w-3xl mx-auto">
            How our AI model performed against the S&P 500 (VOO) over 4 years, including the 2022 bear market
          </p>
          
          <div class="mt-8 inline-flex items-center gap-3 text-sm">
            <span class="opacity-90">Test Period:</span>
            <span class="font-bold bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm">2022-01-01 to 2025-12-24 (4 years)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Winner Banner -->
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl -mt-8">
      <div class="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl shadow-2xl p-8 text-center">
        <div class="text-6xl mb-4">🏆</div>
        <h2 class="text-3xl font-bold mb-2">Winner: InvestBuddy Model</h2>
        <p class="text-xl opacity-90">Beat the S&P 500 by +63.41% over 4 years</p>
        <p class="text-lg opacity-80 mt-2">Transaction-Cost Optimized Monthly Rebalancing</p>
        
        <!-- CTA to Comprehensive Benchmarks -->
        <div class="mt-6 pt-6 border-t border-white/20">
          <router-link 
            to="/research/benchmarks"
            class="inline-flex items-center gap-3 px-8 py-4 bg-white text-green-600 rounded-xl hover:bg-white/90 hover:shadow-2xl transition-all font-bold text-lg group"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
            <span>View Institutional-Grade Multi-Benchmark Analysis</span>
            <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </router-link>
          <p class="text-sm text-white/80 mt-3">📊 Compare against VOO, RSP, QQQ, and 60/40 Portfolio with full transparency</p>
        </div>
      </div>
    </div>

    <!-- Main Results -->
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
      
      <!-- Performance Comparison Grid -->
      <div class="grid md:grid-cols-3 gap-6 mb-12">
        <!-- Total Return -->
        <div class="bg-white rounded-2xl shadow-lg p-8 border-2 border-green-200">
          <div class="text-sm text-slate-600 font-semibold mb-2">Total Return (Monthly)</div>
          <div class="flex items-end justify-between mb-4">
            <div>
              <div class="text-sm text-slate-500 mb-1">VOO</div>
              <div class="text-3xl font-bold text-blue-600">42.71%</div>
            </div>
            <div>
              <div class="text-sm text-slate-500 mb-1">InvestBuddy</div>
              <div class="text-3xl font-bold text-green-600">106.12%</div>
            </div>
          </div>
          <div class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
            <div class="text-2xl font-bold text-green-600">+63.41%</div>
            <div class="text-xs text-slate-600">Alpha over VOO</div>
          </div>
        </div>

        <!-- Sharpe Ratio -->
        <div class="bg-white rounded-2xl shadow-lg p-8 border-2 border-blue-200">
          <div class="text-sm text-slate-600 font-semibold mb-2">Sharpe Ratio (Monthly)</div>
          <div class="text-sm text-slate-500 mb-1">(Risk-Adjusted Returns)</div>
          <div class="flex items-end justify-between mb-4 mt-2">
            <div>
              <div class="text-sm text-slate-500 mb-1">VOO</div>
              <div class="text-3xl font-bold text-blue-600">0.48</div>
            </div>
            <div>
              <div class="text-sm text-slate-500 mb-1">InvestBuddy</div>
              <div class="text-3xl font-bold text-green-600">0.95</div>
            </div>
          </div>
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            <div class="text-2xl font-bold text-blue-600">+0.47</div>
            <div class="text-xs text-slate-600">98% Better</div>
          </div>
        </div>

        <!-- Final Portfolio Value -->
        <div class="bg-white rounded-2xl shadow-lg p-8 border-2 border-purple-200">
          <div class="text-sm text-slate-600 font-semibold mb-2">Final Value (Monthly)</div>
          <div class="text-sm text-slate-500 mb-1">(From $10,000 initial)</div>
          <div class="flex items-end justify-between mb-4 mt-2">
            <div>
              <div class="text-sm text-slate-500 mb-1">VOO</div>
              <div class="text-2xl font-bold text-blue-600">$14,271</div>
            </div>
            <div>
              <div class="text-sm text-slate-500 mb-1">InvestBuddy</div>
              <div class="text-2xl font-bold text-green-600">$20,612</div>
            </div>
          </div>
          <div class="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
            <div class="text-2xl font-bold text-purple-600">+$6,341</div>
            <div class="text-xs text-slate-600">Additional profit</div>
          </div>
        </div>
      </div>

      <!-- Performance Charts - Monthly & Weekly -->
      <div class="space-y-8 mb-12">
        <!-- Monthly Rebalancing Chart -->
        <div class="bg-white rounded-2xl shadow-xl p-8">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-2xl font-bold text-slate-900">📅 Phase 2: Monthly Rebalancing vs VOO (2022–2025)</h2>
            <span class="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-semibold">Winner 🏆</span>
          </div>
          <div class="rounded-lg overflow-hidden bg-slate-50 border border-slate-200">
            <img 
              src="/benchmarks/voo_benchmark_MONTHLY_results.png" 
              alt="Monthly Rebalancing: InvestBuddy vs VOO Performance Comparison" 
              class="w-full h-auto"
            />
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-center gap-6 text-sm">
            <div class="flex items-center gap-2">
              <div class="w-4 h-4 bg-blue-500 rounded"></div>
              <span class="text-slate-600">VOO Buy & Hold</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-4 h-4 bg-red-500 rounded"></div>
              <span class="text-slate-600">InvestBuddy Model</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-4 h-1 bg-slate-300 border-t-2 border-dashed border-slate-400"></div>
              <span class="text-slate-600">Initial Capital ($10,000)</span>
            </div>
          </div>
          <div class="mt-4 p-4 bg-green-50 border-l-4 border-green-500 rounded">
            <p class="text-sm text-slate-700">
              <strong>📊 Monthly Strategy:</strong> Rebalances every 30 days. Best overall performance with 
              <strong class="text-green-700">+63.41% alpha</strong> and only $533 in trading costs.
            </p>
          </div>
        </div>

        <!-- Phase 3: RSP Benchmark (Persistence Hysteresis) -->
        <div class="bg-white rounded-2xl shadow-xl p-8 border-2 border-emerald-300">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-2xl font-bold text-slate-900">🔄 Phase 3: Persistence Hysteresis vs RSP (2023–2025)</h2>
            <span class="px-4 py-2 bg-emerald-100 text-emerald-800 rounded-lg font-semibold">Latest Architecture ✨</span>
          </div>
          <p class="text-sm text-slate-500 mb-6">Equal-weight benchmark — a harder test than market-cap-weighted VOO</p>

          <div class="rounded-lg overflow-hidden bg-slate-50 border border-slate-200">
            <img
              src="/benchmarks/rsp_benchmark_hysteresis_phase3_results.png"
              alt="Phase 3: InvestBuddy vs RSP Equal-Weight Benchmark (Jan 2023 – Dec 2025)"
              class="w-full h-auto"
            />
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-center gap-6 text-sm">
            <div class="flex items-center gap-2"><div class="w-4 h-4 bg-blue-500 rounded"></div><span class="text-slate-600">RSP (Equal-Weight S&P 500)</span></div>
            <div class="flex items-center gap-2"><div class="w-4 h-4 bg-red-500 rounded"></div><span class="text-slate-600">InvestBuddy Phase 3</span></div>
          </div>

          <!-- Phase 3 metrics -->
          <div class="grid md:grid-cols-4 gap-4 mt-6">
            <div class="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200 text-center">
              <div class="text-xs font-semibold text-slate-500 mb-1">Model Return</div>
              <div class="text-3xl font-bold text-emerald-700">+60.80%</div>
            </div>
            <div class="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
              <div class="text-xs font-semibold text-slate-500 mb-1">RSP Return</div>
              <div class="text-3xl font-bold text-blue-600">+35.74%</div>
            </div>
            <div class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200 text-center">
              <div class="text-xs font-semibold text-slate-500 mb-1">Alpha vs RSP</div>
              <div class="text-3xl font-bold text-green-700">+25.05pp</div>
            </div>
            <div class="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200 text-center">
              <div class="text-xs font-semibold text-slate-500 mb-1">Sharpe Ratio</div>
              <div class="text-2xl font-bold text-purple-700">0.78 <span class="text-base text-slate-500">vs 0.64</span></div>
            </div>
          </div>

          <div class="mt-4 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded">
            <p class="text-sm text-slate-700">
              <strong>🔄 Phase 3 Architecture:</strong> Five pipeline fixes — persistence hysteresis (hold winners until signal dies), $5 price floor, 7-day earnings blackout, 25% confidence threshold, head-conflict gate. The critical fix: positions are <em>not</em> sold on a calendar schedule — only when their own signal actually dies. This lets the 5-day momentum window fully materialize before rotating.
            </p>
          </div>

          <div class="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 text-center">
            ⚠️ <strong>Soft OOS:</strong> Model trained through May 2026 — 2023–2025 backtest is validation-split quality. Hard OOS test pending September 2026.
          </div>
        </div>

        <!-- Transaction Cost Analysis -->
        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 border-2 border-blue-200">
          <h2 class="text-2xl font-bold text-slate-900 mb-1">Phase 2: Why Monthly? &middot; Phase 3: Why Weekly?</h2>
          <p class="text-sm text-slate-500 mb-4">The correct rebalancing cadence changed when the model's prediction horizon changed.</p>
          <div class="grid md:grid-cols-3 gap-6 mb-6">
            <div class="bg-white rounded-xl p-6 shadow-sm">
              <div class="text-3xl mb-2">🎯</div>
              <h3 class="font-bold text-slate-900 mb-2">Phase 2: Horizon Alignment</h3>
              <p class="text-sm text-slate-600">Phase 2 predicted <strong>10-day</strong> forward returns. Monthly rebalancing (~30 days) gave those predictions time to materialize fully. 30 days &gt; 10-day horizon = signal had room to play out.</p>
            </div>
            <div class="bg-white rounded-xl p-6 shadow-sm">
              <div class="text-3xl mb-2">💰</div>
              <h3 class="font-bold text-slate-900 mb-2">Phase 2: Cost Evidence</h3>
              <p class="text-sm text-slate-600">Phase 2 data showed $533 in trading costs (monthly) vs $1,224 (weekly). Lower frequency = Sharpe 0.95 vs marginal 0.46. Phase 2's 10-day model couldn't absorb weekly friction efficiently.</p>
            </div>
            <div class="bg-white rounded-xl p-6 shadow-sm">
              <div class="text-3xl mb-2">⚡</div>
              <h3 class="font-bold text-slate-900 mb-2">Phase 3: Weekly Is Correct</h3>
              <p class="text-sm text-slate-600">Retrain #7's primary signal is <strong>day-5</strong> (not day-10). 5 trading days = weekly rebalancing = the model's horizon expires exactly when you rebalance. Persistence hysteresis also caps costs: you only trade when a signal dies, not on a fixed calendar.</p>
            </div>
          </div>
          <!-- Phase 3 cadence note -->
          <div class="bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-4 mt-6">
            <p class="text-sm text-slate-700">
              <strong class="text-emerald-800">⚡ Phase 3 Retrain #7 (current model):</strong> Uses <strong>weekly rebalancing (5 trading days)</strong> — the day-5 prediction horizon and weekly cadence are in perfect structural alignment. Persistence hysteresis means you only trade when a position's signal dies, keeping costs low even at weekly frequency.
            </p>
          </div>
        </div>
      </div>

      <!-- Monthly Rebalancing Table -->
      <div class="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
        <div class="bg-gradient-to-r from-green-600 to-emerald-700 text-white p-6">
          <h2 class="text-2xl font-bold">📅 Monthly Rebalancing Strategy (Every 30 Days)</h2>
          <p class="text-sm opacity-90 mt-1">Optimal balance between performance and costs</p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                <th class="text-left p-4 font-semibold text-slate-900">Metric</th>
                <th class="text-right p-4 font-semibold text-blue-600">VOO Buy & Hold</th>
                <th class="text-right p-4 font-semibold text-green-600">InvestBuddy Model</th>
                <th class="text-right p-4 font-semibold text-slate-900">Difference</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr class="hover:bg-slate-50">
                <td class="p-4 font-medium text-slate-900">Final Value</td>
                <td class="p-4 text-right text-blue-700">$14,271.37</td>
                <td class="p-4 text-right text-green-700 font-bold">$20,612.38</td>
                <td class="p-4 text-right text-green-600 font-semibold">+$6,341.01</td>
              </tr>
              <tr class="hover:bg-slate-50">
                <td class="p-4 font-medium text-slate-900">Total Return</td>
                <td class="p-4 text-right text-blue-700">+42.71%</td>
                <td class="p-4 text-right text-green-700 font-bold">+106.12%</td>
                <td class="p-4 text-right text-green-600 font-semibold">+63.41%</td>
              </tr>
              <tr class="hover:bg-slate-50">
                <td class="p-4 font-medium text-slate-900">Max Drawdown</td>
                <td class="p-4 text-right text-blue-700">-25.41%</td>
                <td class="p-4 text-right text-orange-600">-37.51%</td>
                <td class="p-4 text-right text-red-500 font-semibold">-12.10%</td>
              </tr>
              <tr class="hover:bg-slate-50">
                <td class="p-4 font-medium text-slate-900">Sharpe Ratio</td>
                <td class="p-4 text-right text-blue-700">0.48</td>
                <td class="p-4 text-right text-green-700 font-bold">0.95</td>
                <td class="p-4 text-right text-green-600 font-semibold">+0.47</td>
              </tr>
              <tr class="hover:bg-slate-50">
                <td class="p-4 font-medium text-slate-900">Win Rate</td>
                <td class="p-4 text-right text-slate-400">N/A</td>
                <td class="p-4 text-right text-green-700 font-bold">55.41%</td>
                <td class="p-4 text-right text-slate-400">-</td>
              </tr>
              <tr class="hover:bg-slate-50">
                <td class="p-4 font-medium text-slate-900">Trades</td>
                <td class="p-4 text-right text-blue-700">1</td>
                <td class="p-4 text-right text-slate-700">241</td>
                <td class="p-4 text-right text-slate-600">+240</td>
              </tr>
              <tr class="hover:bg-slate-50">
                <td class="p-4 font-medium text-slate-900">Trading Costs</td>
                <td class="p-4 text-right text-blue-700">$0.00</td>
                <td class="p-4 text-right text-slate-700">$532.87</td>
                <td class="p-4 text-right text-orange-600">-$532.87</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>



      <!-- Honest Quant Transparency Box -->
      <div class="bg-amber-50 rounded-2xl shadow-lg p-8 mb-12 border-2 border-amber-300">
        <div class="flex items-start gap-4 mb-6">
          <div class="text-3xl">🔍</div>
          <div>
            <h2 class="text-2xl font-bold text-slate-900 mb-1">Honest Quant Transparency</h2>
            <p class="text-slate-600 text-sm">We believe exhausted investors deserve platforms that explain <em>why</em> numbers carry risk, not just what the numbers are.</p>
          </div>
        </div>
        <div class="grid md:grid-cols-2 gap-4">
          <div class="bg-white rounded-xl p-5 border border-amber-200">
            <h3 class="font-bold text-slate-800 mb-2">📊 Concentration Risk</h3>
            <p class="text-sm text-slate-600">This AI holds <strong>5 stocks</strong>. VOO holds <strong>500</strong>. Holding fewer stocks amplifies both gains and losses. Our Monte Carlo (1,000 random 5-stock portfolios over the same period) showed the median random portfolio returned <strong>+50.72%</strong> — just from concentration, before any AI at all.</p>
          </div>
          <div class="bg-white rounded-xl p-5 border border-amber-200">
            <h3 class="font-bold text-slate-800 mb-2">🗓️ Historical Simulation</h3>
            <p class="text-sm text-slate-600">The model's parameters were trained on data that overlaps with part of the 2022–2025 test period. This is a <strong>historical simulation</strong>, not a true out-of-sample forward test. True forward testing from March 2026 onwards is ongoing. Results during the 2022 bear market (when InvestBuddy fell harder than VOO to –29%) reflect the model's genuine limitations.</p>
          </div>
          <div class="bg-white rounded-xl p-5 border border-amber-200">
            <h3 class="font-bold text-slate-800 mb-2">📉 Larger Drawdowns</h3>
            <p class="text-sm text-slate-600">InvestBuddy's max drawdown was <strong>–37.51%</strong> vs VOO's <strong>–25.41%</strong>. Higher returns came with higher peak-to-trough losses. If you had started investing in January 2022, you would have experienced a –29% loss by May 2022 before recovering. Past recovery does not guarantee future recovery.</p>
          </div>
          <div class="bg-white rounded-xl p-5 border border-amber-200">
            <h3 class="font-bold text-slate-800 mb-2">🎯 Model Skill vs. Luck</h3>
            <p class="text-sm text-slate-600">Our Monte Carlo showed InvestBuddy at the <strong>77.7th percentile</strong> of random portfolios — beating ~3 in 4 random selections. That's real but moderate edge, not a guaranteed edge. Roughly ~1 in 4 random portfolios achieved similar or better returns through luck alone during this period.</p>
          </div>
        </div>
      </div>

      <!-- Key Performance Highlights -->
      <div class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-8 mb-12 border-2 border-purple-200">
        <h2 class="text-2xl font-bold text-slate-900 mb-6">🎯 Key Performance Highlights</h2>
        <div class="grid md:grid-cols-2 gap-6">
          <div class="bg-white rounded-xl p-6 shadow-sm">
            <h3 class="text-lg font-semibold text-green-700 mb-3">✅ What Worked</h3>
            <ul class="space-y-2 text-slate-700">
              <li class="flex items-start">
                <span class="text-green-500 mr-2">✓</span>
                <span><strong>+63.41% alpha</strong> - Consistently beat VOO over 4 years</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-500 mr-2">✓</span>
                <span><strong>Lower costs:</strong> $533 in trading costs (5.33% of capital)</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-500 mr-2">✓</span>
                <span><strong>Bear market survival:</strong> Recovered from -37.51% drawdown to +106.12% return</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-500 mr-2">✓</span>
                <span><strong>Higher Sharpe:</strong> 0.95 vs 0.48 (98% better risk-adjusted returns)</span>
              </li>
            </ul>
          </div>
          <div class="bg-white rounded-xl p-6 shadow-sm">
            <h3 class="text-lg font-semibold text-purple-700 mb-3">📊 The Numbers Don't Lie</h3>
            <ul class="space-y-2 text-slate-700">
              <li class="flex items-start">
                <span class="text-purple-500 mr-2">•</span>
                <span><strong>55.41% OOS win rate</strong> - Model directional accuracy (5,356 predictions)</span>
              </li>
              <li class="flex items-start">
                <span class="text-purple-500 mr-2">•</span>
                <span><strong>+106.12% portfolio return</strong> - Monthly rebalancing simulation</span>
              </li>
              <li class="flex items-start">
                <span class="text-purple-500 mr-2">•</span>
                <span><strong>241 trades</strong> - Over 1,003 trading days (~4 years)</span>
              </li>
              <li class="flex items-start">
                <span class="text-purple-500 mr-2">•</span>
                <span><strong>Survived 2022 bear market</strong> - Real stress test</span>
              </li>
            </ul>
          </div>
        </div>
        <div class="mt-6 p-4 bg-green-100 rounded-lg border-l-4 border-green-500">
          <p class="text-slate-800">
            <strong>💡 Key Insight:</strong> Monthly rebalancing aligns with our LSTM's 10-day prediction horizon, giving signals time to fully materialize. Phase 2's AI is strong enough that weekly trading still beats the S&P 500 — but at the cost of a Sharpe collapse from 0.95 to 0.46 and 130% more in broker fees. Monthly is the <strong>elite risk-adjusted choice</strong>, not just the cheaper one.
          </p>
        </div>
      </div>

      <!-- Interpretation Section -->
      <div class="grid md:grid-cols-3 gap-6 mb-12">
        <div class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-300">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl">
              ✅
            </div>
            <h3 class="text-xl font-bold text-slate-900">Total Return</h3>
          </div>
          <p class="text-slate-700 font-semibold mb-2">Model outperformed VOO</p>
          <p class="text-3xl font-bold text-green-600 mb-2">+63.41% Alpha</p>
          <p class="text-sm text-slate-600">$+6,341 additional profit (monthly)</p>
        </div>

        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-300">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl">
              ✅
            </div>
            <h3 class="text-xl font-bold text-slate-900">Risk-Adjusted Returns</h3>
          </div>
          <p class="text-slate-700 font-semibold mb-2">Better Sharpe ratio</p>
          <p class="text-3xl font-bold text-blue-600 mb-2">0.95 vs 0.48</p>
          <p class="text-sm text-slate-600">98% better risk-adjusted performance</p>
        </div>

        <div class="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border-2 border-orange-200">
          <div class="flex items-center gap-3 mb-4">
            <div class="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl">
              ⚡
            </div>
            <h3 class="text-xl font-bold text-slate-900">Max Drawdown</h3>
          </div>
          <p class="text-slate-700 font-semibold mb-2">Higher return, higher volatility</p>
          <p class="text-3xl font-bold text-orange-600 mb-2">-37.51% vs -25.41%</p>
          <p class="text-sm text-slate-600">Offset by +63.41% alpha over 4 years</p>
        </div>
      </div>

      <!-- Strategy Details -->
      <div class="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-8 mb-12 border border-purple-200">
        <h2 class="text-2xl font-bold text-slate-900 mb-6">Strategy Details</h2>
        
        <div class="bg-white rounded-xl p-6 shadow-md mb-6">
          <h3 class="text-lg font-bold text-indigo-600 mb-4">InvestBuddy Model Strategy</h3>
          <ul class="space-y-3">
            <li class="flex items-start gap-3">
              <svg class="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span class="text-slate-700">Rebalance every <strong>30 calendar days</strong></span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span class="text-slate-700">Portfolio: Top <strong>5 ML-predicted stocks</strong> (LSTM Ranker)</span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span class="text-slate-700"><strong>Equal-weight allocation</strong> across all 5 positions</span>
            </li>
            <li class="flex items-start gap-3">
              <svg class="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
              <span class="text-slate-700">Transaction costs: <strong>$1.0/trade + 0.05% slippage</strong></span>
            </li>
          </ul>
        </div>

        <div class="bg-green-100 border-2 border-green-300 rounded-xl p-6">
          <div class="flex items-start gap-3">
            <svg class="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <div>
              <p class="font-bold text-green-900 mb-1">Production LSTM Model</p>
              <p class="text-sm text-green-800">Phase 2 LSTM trained on 46 features (phase2_lstm_20260310_100139). Achieves <strong>55.41% OOS directional win rate</strong> across 5,356 independent predictions (85 symbols, Dec 2025–Mar 2026, p &lt; 0.0001). Monthly rebalancing portfolio simulation: <strong>+106.12% return</strong> vs VOO +42.71%, Sharpe 0.95.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Marketing Angle -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white text-center mb-12">
        <h2 class="text-3xl font-bold mb-4">The Bottom Line</h2>
        <p class="text-xl opacity-95 max-w-3xl mx-auto mb-6">
          "Our ML model beat the S&P 500 by <strong class="text-yellow-300">+63.41%</strong> over 4 years (monthly rebalancing), including the 2022 bear market."
        </p>
        <p class="text-lg opacity-90 mb-4">
          Better returns. Better risk-adjusted performance. Transaction-cost optimized. Real results.
        </p>
        <p class="text-sm opacity-80">
          Monthly rebalancing aligns with our LSTM's 10-day prediction horizon • $533 in costs (5.33% of capital) • Empirically validated against 5-day, 7-day, and 14-day frequencies
        </p>
      </div>

      <!-- CTA Section -->
      <div class="bg-white rounded-2xl shadow-xl p-12 text-center">
        <h2 class="text-3xl font-bold text-slate-900 mb-4">Ready to Try It Yourself?</h2>
        <p class="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
          Start with a free 7-day trial. No credit card required. See how our AI can help optimize your portfolio.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <router-link 
            to="/login" 
            class="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all font-semibold text-lg group"
          >
            Start Free Trial
            <svg class="inline-block w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </router-link>
          <router-link 
            to="/transparency" 
            class="px-8 py-4 border-2 border-purple-300 bg-white text-purple-700 rounded-xl hover:border-purple-600 hover:bg-purple-50 transition-all font-semibold text-lg"
          >
            See Validation Report
          </router-link>
        </div>
      </div>

      <!-- Disclaimer -->
      <div class="mt-12 bg-slate-100 rounded-xl p-6 border border-slate-200">
        <p class="text-xs text-slate-600 text-center">
          <strong>Disclaimer:</strong> Past performance does not guarantee future results. This is a historical backtest and not financial advice. 
          All investments carry risk. The results shown are based on historical data from <strong>January 1, 2022 to December 31, 2025</strong> (4 years) and include $1/trade transaction costs with 0.05% slippage.
          Test period includes a 60-day warmup period where the model remains in cash until sufficient historical data is available.
          Individual results may vary. Please consult with a financial advisor before making investment decisions.
        </p>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';

onMounted(() => {
  // Track page view
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: '/benchmarks',
      page_title: 'VOO Benchmark Results'
    });
  }
});
</script>

<style scoped>
/* Smooth scroll behavior */
html {
  scroll-behavior: smooth;
}
</style>
